const equals = (a, b) => {
    if(a.length === b.length) {
    	return a.every((v, i) => v === b[i]); 
    }      

    return false;
}

class ProminenceCalculationData {
    constructor(minLeftIndex, minRightIndex, minLeft, minRight, threshold) {
        this.minLeftIndex = minLeftIndex;
        this.minRightIndex = minRightIndex;
        this.minLeft = minLeft;
        this.minRight = minRight;
        this.threshold = threshold;
    }
}

export default class Peak {
    constructor(signal, m, l, r, mode) {
        if (mode != "peak" && mode != "trough") {
            // throw new IllegalArgumentException("mode must be peak or trough");
            return null;
        }

        this.signal = signal.map(Number);

        // Peak Information
        this.midpoints = m;

        // Peak Height and Plateau Information
        this.height = new Array(m.length).fill(0.0);

        //(Equivalent to scipy.signal.find_peaks() plateau_size parameter)
        this.plateau_size = new Array(m.length).fill(0);

        for (let i = 0; i < m.length; i++) {
            if (mode == "peak") {
                this.height[i] = signal[this.midpoints[i]];
            }
            else {
                this.height[i] = 0 - signal[this.midpoints[i]];
            }

            this.plateau_size[i] = Math.abs(r[i] - l[i] + 1);
        }

        // Peak Distance Information (Equivalent to scipy.signal.find_peaks() distance parameter)
        this.distance = this.findPeakDistance(this.midpoints);

        // Peak Sharpness Information (Equivalent to scipy.signal.find_peaks() threshold parameter)
        this.sharpness = this.findPeakSharpness(this.midpoints);

        // Peak Prominence Information (Equivalent to scipy.signal.find_peaks() prominence parameter)
        // Refer to https://uk.mathworks.com/help/signal/ug/prominence.html

        this.prominenceData = this.findPeakProminence(this.midpoints);
        this.prominence = this.prominenceData[0];

        // Peak Width Information (Equivalent to scipy.signal.find_peaks() width parameter)
        // Refer to https://docs.scipy.org/doc/scipy/reference/generated/scipy.signal.peak_widths.html
        this.relative_height = 0.5;
        this.widthData = this.findPeakWidth(this.midpoints, this.relative_height);
        this.width = this.widthData[0];
    }

    findPeakHeights(peaks) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        let newHeight = new Array(peaks.length).fill(0.0);

        for (let i = 0; i < peaks.length; i++) {
            newHeight[i] = this.signal[peaks[i]];
        }

        return newHeight;
    }

    findPlateauSize(peaks) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        let peak_indices = this.getIndexFromPeak(peaks);
        let newPS = new Array(peaks.length).fill(0);

        for (let i = 0; i < peak_indices.length; i++) {
            newPS[i] = this.plateau_size[peak_indices[i]];
        }

        return newPS;
    }

    findPeakSharpness(peaks) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        let sharpness = Array.from(Array(2), () => new Array(peaks.length).fill(0.0))

        for (let i = 0; i < peaks.length; i++) {
            sharpness[0][i] = this.signal[peaks[i]] - this.signal[peaks[i]-1];
            sharpness[1][i] = this.signal[peaks[i]] - this.signal[peaks[i]+1];
        }

        return sharpness;
    }

    findPeakDistance(peaks) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        return this.diff(peaks);
    }

    findPeakProminence(peaks) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        let prominence = new Array(peaks.length).fill(0.0);
        let left_base = new Array(peaks.length).fill(0.0);
        let right_base = new Array(peaks.length).fill(0.0);
        let pcd = new Map();

        // first: update pcd map with left and right min data (index and value) between peaks.
        let signalIndex = 0;
        let peakIndex = 0;

        while (signalIndex < this.signal.length && peakIndex < peaks.length) {
            let nextSignalIndex = peaks[peakIndex];
            let minLeftIndex = nextSignalIndex;
            let minLeft = this.signal[nextSignalIndex];

            for (let i = nextSignalIndex - 1; i >= signalIndex; i--) {
                if (this.signal[i] < minLeft) {
                    minLeft = this.signal[i];
                    minLeftIndex = i;
                }
            }

            let minRightIndex = nextSignalIndex;
            let minRight = this.signal[nextSignalIndex];
            let lastRightIndex = (peakIndex + 1 < peaks.length) ? peaks[peakIndex + 1] : this.signal.length - 1;

            for (let i = nextSignalIndex + 1; i <= lastRightIndex; i++) {
                if (this.signal[i] < minRight) {
                    minRight = this.signal[i];
                    minRightIndex = i;
                }
            }

            let th = this.signal[peaks[peakIndex]];

            pcd.set(peakIndex, new ProminenceCalculationData(minLeftIndex, minRightIndex, minLeft, minRight, th));
            signalIndex = nextSignalIndex;
            peakIndex++;
        }

        // second: find the nearest left and right minima based threshold.
        let ref = 0;
        while (ref < pcd.size) {
            let finalRef = ref;
            let thRef = pcd.get(finalRef).threshold;

            let relativeLeftProminence = pcd.get(ref);

            for (let i = ref - 1; i >= 0; i--) {
              // if threshold is reached means a left peak is it.
              if (pcd.get(i).threshold >= thRef) {
                  break;
              }

              if (pcd.get(i).minLeft < relativeLeftProminence.minLeft) {
                  relativeLeftProminence = pcd.get(i);
              }
            }

            let relativeRightProminence = pcd.get(ref);

            for (let i = ref + 1; i < pcd.length; i++) {
                // if threshold is reached means a right peak is it.
                if (pcd.get(i).threshold >= thRef) {
                    break;
                }

                if (pcd.get(i).minRight < relativeRightProminence.minRight) {
                    relativeRightProminence = pcd.get(i);
                }
            }

            left_base[finalRef] = relativeLeftProminence.minLeftIndex;
            right_base[finalRef] = relativeRightProminence.minRightIndex;

            prominence[finalRef] = Math.min(thRef - relativeLeftProminence.minLeft, thRef - relativeRightProminence.minRight);

            ref++;
        }

        const promData = Array.from(Array(3), () => new Array(peaks.length).fill(0.0));

        promData[0] = prominence;
        promData[1] = left_base;
        promData[2] = right_base;

        return promData;
    }

    findPeakWidth(peaks, rel_height) {
        if (!equals(this.midpoints, peaks)) {
            peaks.sort();
            this.getIndexFromPeak(peaks);
        }
        else {
            peaks = this.midpoints;
        }

        if (rel_height > 1.0 || rel_height < 0.0) {
            // throw new IllegalArgumentException("rel_height can be between 0.0 and 1.0");
            return null;
        }
        
        let width = new Array(peaks.length).fill(0.0);
        let promData = this.prominenceData; //this.findPeakProminence(peaks);
        let prominence = promData[0];
        let left_bases = promData[1];
        let right_bases = promData[2];

        let widthHeight = new Array(peaks.length).fill(0.0);
        let leftIntersectPoint = new Array(peaks.length).fill(0.0);
        let rightIntersectPoint = new Array(peaks.length).fill(0.0);

        for (let i = 0; i < peaks.length; i++) {
            widthHeight[i] = this.signal[peaks[i]] - prominence[i] * rel_height;

            // Calculate left intersection point of half width
            let j = peaks[i];

            while (left_bases[i] < j && widthHeight[i] < this.signal[j]) {
                j--;
            }

            leftIntersectPoint[i] = j;

            if (this.signal[j] < widthHeight[i]) {
                leftIntersectPoint[i] += (widthHeight[i] - this.signal[j])/(this.signal[j+1] - this.signal[j]);
            }

            // Calculate right point of half width
            j = peaks[i];

            while (j < right_bases[i] && widthHeight[i] < this.signal[j]) {
                j++;
            }

            rightIntersectPoint[i] = j;

            if (this.signal[j] < widthHeight[i]) {
                rightIntersectPoint[i] -= (widthHeight[i] - this.signal[j])/(this.signal[j-1] - this.signal[j]);
            }

            width[i] = rightIntersectPoint[i] - leftIntersectPoint[i];
        }

        const wData = Array.from(Array(4), () => new Array(peaks.length).fill(0.0));

        wData[0] = width;
        wData[1] = widthHeight;
        wData[2] = leftIntersectPoint;
        wData[3] = rightIntersectPoint;

        return wData;
    }

    getPeaks() {
        return this.midpoints;
    }

    getIndexFromPeak(peaks) {
        let indices = new Array(peaks.length).fill(0);
        let indexCounter = 0;
        let i = 0;
        let j = 0;

        while ((i < this.midpoints.length) && (j < peaks.length)) {
            if (this.midpoints[i] == peaks[j]) {
                indices[j] = i;
                indexCounter++;
                i++;
                j++;
            }
            else {
                i++;
            }
        }

        if (indexCounter != peaks.length) {
            // throw new IllegalArgumentException("Peaks in argument does not exist in the original peak list");
            return null
        }

        return indices;
    }

    diff(arr) {
        if (arr.length == 0) {
            return new Array();
        }

        let sig = new Array(arr.length-1).fill(0.0);

        for (let i = 0; i < sig.length; i++) {
            sig[i] = (arr[i+1] - arr[i]);
        }

        return sig;
    }
}